import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode,
} from "react";
import { useT, type Werte } from "../i18n";
import { aufNurLesen } from "../daten/nurlesen";

/* ══════════════════════════════════════════════════════════════════
   Die Rückmeldung — sie zeigt, dass etwas passiert ist.

   Sie sagt nicht „Gespeichert", sondern was daraus folgt: „Belegung
   verschoben. Das alte Fenster ist sofort wieder frei." Die Sätze
   stehen unter `meldung.*` in de.json; hier steht nur, wie lange sie
   stehen bleiben.
   ══════════════════════════════════════════════════════════════════ */

type Art = "gut" | "acht" | "stopp";
/**
 * `bleibt`: Die Meldung geht nicht von selbst, sondern mit „Gesehen"
 * (Prüfrunde Interview F, F35-Rest, 05.10.2026). Für den einen Satz, der nach
 * dem Weiterspringen noch gelesen werden muss — „Vorgang storniert … die
 * Sperre aus dem Gerätetausch bleibt: KR-04 ab 10.11." Die Maske sprang
 * danach auf die Auftragsliste, und nach 3,4 Sekunden war der Satz weg,
 * bevor die Liste stand.
 */
type Wie = { bleibt?: boolean };
type Rufer = (schluessel: string, werte?: Werte, art?: Art, wie?: Wie) => void;

const Raum = createContext<Rufer | null>(null);

export function RufRaum({ children }: { children: ReactNode }) {
  const [stand, setzeStand] = useState<{ text: string; art: Art; nr: number; bleibt: boolean } | null>(null);
  const t = useT();
  const uhr = useRef<number | undefined>(undefined);

  const rufen = useCallback<Rufer>((schluessel, werte, art, wie) => {
    const text = t(`meldung.${schluessel}`, werte);
    // Die Art steckt schon im Satz: „gelöscht" ist nicht „angelegt".
    // Wer sie nicht mitgibt, bekommt die freundliche.
    setzeStand({ text, art: art ?? "gut", nr: Date.now(), bleibt: wie?.bleibt === true });
  }, [t]);

  /* Ein Schreibversuch, den der Nur-Lese-Riegel abgewiesen hat (P6-01). */
  useEffect(() => aufNurLesen((art) => rufen(art === "pruefer" ? "nurLesenPruefer" : "nurLesenMandant", undefined, "acht")), [rufen]);

  useEffect(() => {
    if (!stand) return;
    window.clearTimeout(uhr.current);
    if (stand.bleibt) return;
    uhr.current = window.setTimeout(() => setzeStand(null), 3400);
    return () => window.clearTimeout(uhr.current);
  }, [stand]);

  const wert = useMemo(() => rufen, [rufen]);

  return (
    <Raum.Provider value={wert}>
      {children}
      {stand ? (
        // `role="status"` statt `alert`: Die Meldung unterbricht nichts,
        // sie berichtet. Ein Alert würde den Schirmleser mitten im Satz
        // abschneiden.
        <div className={`ruf${stand.art === "gut" ? "" : ` ${stand.art}`}${stand.bleibt ? " bleibt" : ""}`} role="status">
          <span className={`pkt ${stand.art}`} />
          {stand.text}
          {stand.bleibt ? (
            <button type="button" className="ruf-gesehen" onClick={() => setzeStand(null)}>
              {t("allgemein.gesehen")}
            </button>
          ) : null}
        </div>
      ) : null}
    </Raum.Provider>
  );
}

export function useRuf(): Rufer {
  const wert = useContext(Raum);
  if (!wert) throw new Error("useRuf braucht einen RufRaum darüber.");
  return wert;
}
